import { ComponentProps, forwardRef } from 'react'
import { BubbleBlock, type CommonBlockProps } from '../../../types'
import { Block } from '../../Block'
import { webchatClasses } from '../../../styles/classes'
import { useModalContext } from '../../../contexts'
import { ThumbsDown, ThumbsUp } from 'lucide-react'

type Props = BubbleBlock & CommonBlockProps & ComponentProps<'div'>

export const Bubble = forwardRef<HTMLDivElement, Props>(
  (
    {
      block,
      direction,
      sender,
      timestamp,
      messageId,
      feedback,
      metadata,
      isReadOnly,
      sendMessage,
      addMessageFeedback,
      ...props
    },
    ref
  ) => {
    const { showInputModal } = useModalContext()

    const {
      message: {
        blocks: {
          bubble: { container, feedbackContainer, feedbackIcon },
        },
      },
    } = webchatClasses

    if (direction == 'incoming') {
      return (
        <div data-direction={'incoming'} data-feedback={!!feedback} {...props} {...container} ref={ref}>
          <Block
            block={{
              ...block,
              direction,
              timestamp,
              sender,
              messageId,
              isReadOnly,
              metadata,
              sendMessage,
            }}
          />

          {addMessageFeedback && showInputModal && block.type === 'text' && (
            <div {...feedbackContainer}>
              <ThumbsUp
                {...feedbackIcon}
                data-selected={feedback === 'positive'}
                onClick={() => addMessageFeedback(messageId, { value: 'positive' })}
              />
              <ThumbsDown
                {...feedbackIcon}
                data-selected={feedback === 'negative'}
                onClick={async () => {
                  const feedbackComment = await showInputModal({
                    title: 'Help us improve',
                    description: 'Please provide feedback to help us improve.',
                    confirmButtonText: 'Send feedback',
                    cancelButtonText: 'Cancel',
                    Content: FeedbackForm,
                  })

                  addMessageFeedback(messageId, { value: 'negative', comment: feedbackComment })
                }}
              />
            </div>
          )}
        </div>
      )
    } else {
      return (
        <div data-direction={'outgoing'} {...props} {...container} ref={ref}>
          <Block
            block={{
              ...block,
              direction,
              timestamp,
              sender,
              messageId,
              isReadOnly,
              metadata,
              sendMessage,
              addMessageFeedback,
            }}
          />
        </div>
      )
    }
  }
)
Bubble.displayName = 'Bubble'

type FeedbackFormProps = {
  setValue: (comment: string) => void
}

const FeedbackForm = ({ setValue }: FeedbackFormProps) => {
  const {
    modal: {
      dialog: { content },
    },
  } = webchatClasses

  return (
    <textarea
      {...content.textarea}
      placeholder="Let us know what went wrong..."
      onChange={(e) => setValue(e.target.value)}
    ></textarea>
  )
}
